//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { TopAppBar } from '@react/topbar/TopAppBar'
import React from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'

import { Button, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import AdminCompanyProfileList from '@react/admin/AdminCompanyProfileList'
import DemoManagement from '@react/admin/DemoManagement'
import { PoolCreateMultiStepForm } from '@react/admin/PoolCreate/PoolCreateMultiStepForm'
import ExtraWidthLimited from '@react/components/ExtraWidthLimited'
import Width1200 from '@react/components/Width1200'
import Width1600 from '@react/components/Width1600'
import WidthLimited from '@react/components/WidthLimited'
import ReportingPage from '@react/reporting/ReportingPage'
import { logOT } from '@shared/loggingShared'
import LytIssuerPoolDetails from './LytIssuerPoolDetails'
import LytIssuerReserves from '@react/lyt/planning/LytIssuerReserves'
import { LytTransparency } from '@react/lyt/LytTransparency'
import { useOtChainState } from '@react/redux/store'
import AdminLiquidityAssets from '@react/admin/AdminLiquidityAssets'

export default function LytIssuerRouting(): React.ReactElement {
  const theme = useTheme()
  logOT.debug('AdminRouting')
  const navigate = useNavigate()
  const {
    stageConfiguration: { lytPoolAddr },
  } = useOtChainState()
  return (
    <Box
      data-testid="LytIssuerRouting"
      sx={{ height: '100vh', width: '100%', backgroundColor: theme.palette.background.canvas, overflow: 'scroll' }}
    >
      <TopAppBar></TopAppBar>
      <Box sx={{ flexDirection: 'row', maxWidth: '100%', display: 'flex', gap: 2 }}>
        <Button onClick={() => navigate('/')}>
          <Typography variant="h5">Overview</Typography>
        </Button>
        <Button onClick={() => navigate('/reservePlanning')}>
          <Typography variant="h5">Reserve Planning</Typography>
        </Button>
        <Button onClick={() => navigate('/transparency')}>
          <Typography variant="h5">Transparency</Typography>
        </Button>
        <Button onClick={() => navigate('/reports')}>
          <Typography variant="h5">Reports</Typography>
        </Button>
      </Box>

      <Routes>
        <Route
          path="/"
          element={
            <Width1200>
              <LytIssuerPoolDetails />
            </Width1200>
          }
        />
        <Route
          path="/reservePlanning"
          element={
            <Width1600>
              <LytIssuerReserves />
            </Width1600>
          }
        />
        <Route
          path="/transparency"
          element={
            <Width1600>
              <LytTransparency poolAddr={lytPoolAddr} />
            </Width1600>
          }
        />
        <Route
          path="/reports"
          element={
            <Box data-testid="AdminRoundBox2" sx={{ margin: 'auto', marginTop: 3, height: '100vh', maxWidth: '100%' }}>
              <Box sx={{ height: '60px', display: 'flex', justifyContent: 'space-between', paddingLeft: '15px' }}>
                <Typography align="left" sx={{ display: 'inline', float: 'left', fontWeight: 'bold' }} variant={'h4'}>
                  Reports
                </Typography>
              </Box>
              <ReportingPage />
            </Box>
          }
        />

        <Route
          path="/pools/add/v2"
          element={
            <WidthLimited>
              <PoolCreateMultiStepForm />
            </WidthLimited>
          }
        />
        <Route
          path="/liquidityAssets"
          element={
            <Width1600>
              <AdminLiquidityAssets />
            </Width1600>
          }
        />
        <Route
          path="/pools/:poolAddr"
          element={
            <Width1200>
              <LytIssuerPoolDetails />
            </Width1200>
          }
        />
        <Route path="/companyProfiles" element={<AdminCompanyProfileList />} />
        <Route
          path="/demo"
          element={
            <ExtraWidthLimited>
              <DemoManagement />
            </ExtraWidthLimited>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  )
}
